// Type-aware default width floors/caps for NORMAL record columns (the record's
// own fields: name, location, timestamps, user references, id), mirroring the
// per-attribute-column widths in AttributeValueSchema.getAttributeColumnCellStyle.
//
// WHY: with native table auto-layout a column's width is driven by the intrinsic
// content width of the first rendered rows. A min-width FLOOR on every column
// keeps each one readable and stops any column being squeezed to nothing when the
// table is space-constrained (so the layout stays understandable regardless of row
// content). A max-width CAP, where present, additionally bounds how wide a column
// grows so a long-content column can't dominate the row; the cap ellipsizes long
// cells (RecordLabel is nowrap + text-overflow: ellipsis) rather than overflowing.
//
// The leading Name column carries a floor plus a generous max-width cap, like
// every other column — the cap bounds only the AUTO-grown width. The Name cell
// renders the name and (in Search / Advanced-Search result rows) a grey
// description subtitle on a second line, both single-line. Auto-layout grows the
// column with that content; the cap stops it at a ceiling and the cell ellipsizes
// beyond it (RecordLabel is nowrap + text-overflow: ellipsis), so a long name — or
// a long description subtitle in the same cell — cannot stretch the column into a
// multi-thousand-px horizontal scroll. The cap is deliberately generous (wider
// than any other column) so most names show in full within the auto-grown width.
// A user drag-resize pins the column to ANY chosen width, above or below the cap:
// tableUtils merges a persisted width as width==min==max, replacing the cap
// entirely, so a user who wants a wider Name column can still have one
// (Margot, operator-task-fe-search-name-column-description-blowout-regime1).
//
// Keyed by the legacy column `key`. Returns a `cellStyle` in the shape RecordTable
// threads onto BOTH the body and header cell (`{ style: { minWidth, maxWidth } }`),
// so the width constraint holds symmetrically (a long header label can't force a
// capped column past its cap). A floor-only column (one with no `maxWidth`) emits
// `maxWidth: "none"`; every configured column here carries a numeric cap. Unknown
// keys return `undefined` (no constraint) so only the record columns we intend to
// size are affected.
const RECORD_COLUMN_WIDTH: Record<string, { minWidth: number; maxWidth?: number }> = {
    // The leading record column. Generous floor (badges + name) and a generous
    // auto-grow cap: the column grows with its content up to the cap (showing most
    // names in full) and ellipsizes beyond it, so neither a long name nor the grey
    // description subtitle rendered in the same cell can stretch the column into a
    // multi-thousand-px horizontal scroll. The cap is the widest of any column so
    // the common case reads fully; a user drag-resize overrides it either way.
    name: { minWidth: 180, maxWidth: 560 },
    // Location reference (project/bundle/workspace label) — a short-ish reference.
    location: { minWidth: 120, maxWidth: 220 },
    // Timestamps render as a relative "… ago" label — narrow and fixed-ish.
    created_at: { minWidth: 110, maxWidth: 170 },
    modified_at: { minWidth: 110, maxWidth: 170 },
    // User references (identity label + icon) — bounded like other name-ish cells.
    created_by: { minWidth: 120, maxWidth: 200 },
    modified_by: { minWidth: 120, maxWidth: 200 },
    // UUID id column (monospace, hidden by default) — bounded so it can't hog width.
    id: { minWidth: 130, maxWidth: 280 },
    // Free-text / markdown description — clipped in the cell renderer; matches the
    // large-content attribute-column width (form-markdown) so an added Description
    // column reads consistently and its long content can't hog the row.
    description: { minWidth: 260, maxWidth: 480 },
    // record_log.subject — the main free-text column of the execution logs table.
    // Bounded so the subject renders on a SINGLE line and ellipsizes to the column
    // width (via the OverflowTooltip cell renderer) instead of wrapping to a second
    // line, and so it can't push the whole table into a page-level horizontal scroll.
    subject: { minWidth: 240, maxWidth: 480 },
    // Execution status: badge on the first line, the status message (single line,
    // ellipsized at the column width, full text in the hover popover) on the
    // second. The message's overflow:hidden gives it no min-content width, so
    // without a floor a space-constrained table squeezes the column to the ~100px
    // badge. The column auto-grows with the message up to the cap.
    status: { minWidth: 200, maxWidth: 360 },
    // Execution info: the started-from reference on the first line and the
    // state line (runtime, waits, line number) on the second. Grows with its
    // content up to the cap and ellipsizes each line at the column width.
    info: { minWidth: 200, maxWidth: 360 },
    // Inline row-action icons (InlineActionButtons / bundle BundleActionToolbar).
    // Floor = a comfortable row of the common ~3-4 icons; cap so a busy row's
    // action set can't auto-widen the column and hog the table. The inner
    // .inlineActionButtons row has min-width:0 + overflow:hidden so the column
    // honours this cap and CLIPS the trailing icons (left-aligned) instead of
    // widening; a right-edge fade cue signals the overflow and the column can be
    // drag-resized wider to reveal them (Margot, #2/#4,
    // operator-task-fe-list-view-row-height-harmonize-real).
    actions: { minWidth: 108, maxWidth: 168 },
};

/**
 * Resolve a `cellStyle` (`{ style: { minWidth, maxWidth } }`) for a normal record
 * column by its legacy column `key`. A configured column always carries a min-width
 * floor. `maxWidth` is ALWAYS emitted:
 *  - a px cap for a column with a configured `maxWidth` (all columns here), and
 *  - the keyword `"none"` for a floor-only column (one with no `maxWidth`).
 *
 * The inline `max-width` matters because the leading record column's <td> also
 * carries the legacy class `nameCell` (`.table .nameCell` in Table.scss), which
 * governs that cell's nowrap/overflow/ellipsis. Threading the cap inline onto the
 * <td> (inline styles outrank the class selector) sizes the Name column exactly as
 * this policy intends — scoped to the record columns recordColumnWidths governs —
 * while the shared `.nameCell` rule handles the ellipsis within whatever width the
 * column settles at (the cap, or a user drag-resize width).
 *
 * Returns `undefined` for keys with no configured width so unrelated columns keep
 * their default auto-layout behaviour.
 */
export const getRecordColumnCellStyle = (
    key: string
): { style: { minWidth: number; maxWidth: number | "none" } } | undefined => {
    const width = RECORD_COLUMN_WIDTH[key];
    if (!width) {
        return undefined;
    }
    return {
        style: {
            minWidth: width.minWidth,
            maxWidth: typeof width.maxWidth === "number" ? width.maxWidth : "none",
        },
    };
};
